import { get } from "@rails/request.js";

type AutoCompleteConfig = {
  autoCompleteUrl: string;
  autoCompleteQueryParam: string;
  autoCompleteMinLength: string;
  autoCompleteInputName: string;
};

const optionSelector = "[role='option']:not([aria-disabled])";

export default (inputValue = "") => ({
  activeOption: null,
  selectedOption: null,
  selectedValue: "",
  config: {},
  results: {
    loading: false,
    content: "",
    visible: false,
  },
  showClear: false,

  init() {
    this.config = this.$el.dataset as AutoCompleteConfig;
    this.selectedValue = inputValue;

    // Show clear button if there's a pre-populated value
    if (inputValue && inputValue.length > 0) {
      this.showClear = true;
    }

    this.$anchor(
      this.$refs.inputContainer,
      this.$refs.resultsContainer,
      null,
      null,
      true
    );

    this.$watch("results.visible", () => {
      if (this.results.visible) {
        this.showClear = true;
      } else {
        this.activeOption = null;
        this.results.content = "";
      }
      this.$dispatch("auto-complete:toggle", {
        action: this.results.visible ? "open" : "close",
        inputName: this.config.autoCompleteInputName,
      });
    });
  },

  /**
   * Determines if the provided option in the listbox is active in the sense
   * that the user is hovered over the option, and one click or keystroke away
   * from selecting the option.
   */
  isActive(itemEl: Element) {
    return this.activeOption?.id === itemEl.id;
  },

  /**
   * Determines if the provided option in the listbox is selected.
   */
  isSelected(itemEl: Element) {
    return (
      this.selectedValue === itemEl.getAttribute("data-listbox-option-value")
    );
  },

  onInputChange() {
    this.selectedValue = "";

    const query = this.$refs.searchInput.value.trim();

    if (query && query.length >= this.config.autoCompleteMinLength) {
      this.fetchResults(query);
    } else {
      this.resetResults();
      if (query.length === 0) {
        this.showClear = false;
        this.$dispatch("auto-complete:clear", {
          inputName: this.config.autoCompleteInputName,
        });
      }
    }
  },

  onResultsClick(event: any) {
    if (!(event.target instanceof Element)) {
      return;
    }
    const selected = event.target.closest(optionSelector);
    if (selected) {
      this.commit(event.target);
    }
  },

  /**
   * Selects the active item on enter key press.
   */
  onEnterKeydown() {
    if (this.activeOption && this.results.visible) {
      this.commit(this.activeOption);
    }
  },

  /**
   * Hides the dropdown on clicks outside the component. If there is no pre-
   * selected value, clears the search input value and hides the clear button.
   */
  onClickOutside() {
    this.resetResults();

    if (this.selectedValue === "") {
      this.$refs.searchInput.value = "";
      this.showClear = false;
    }
  },

  /**
   * Clears the selected item, resets the search result list, clears the search
   * query input field, and hides the clear button, effectively setting the
   * auto complete back to its initial state.
   */
  clear() {
    this.$refs.searchInput.value = "";
    this.$refs.searchInput.focus();
    this.selectedValue = "";
    this.resetResults();
    this.showClear = false;
    this.$dispatch("auto-complete:clear", {
      inputName: this.config.autoCompleteInputName,
    });
  },

  /**
   * Clears the selected item and the search query input field, effectively
   * setting the auto complete back to its initial state. Works similar to the
   * clear method above but for externally-triggered resets such as a reset
   * button on wrapping form, which doesn't require focus to be returned to the
   * search input, or the search results list to be hidden.
   */
  reset() {
    this.$refs.searchInput.value = "";
    this.selectedValue = "";
    this.showClear = false;
  },

  /**
   * Returns a sibling element from the list of result options.
   *
   * This is used by keyboard events to support navigation through
   * results via the up/down arrow keys.
   *
   * @param next - If true, returns next sibling, else returns the previous.
   */
  selectSibling(next: boolean) {
    const options = this.options;
    const selected = this.activeOption;
    const index = options.indexOf(selected);
    const sibling = next ? options[index + 1] : options[index - 1];
    const defaultSibling = next ? options[0] : options[options.length - 1];

    this.activeOption = sibling || defaultSibling;
    if (this.activeOption) this.activeOption.scrollIntoView(false);
  },

  /**
   * Persists the active selection
   *
   * Scenario A: Links
   * If the result items are anchors, we simply click the link and
   * allow the link to be followed.
   *
   * Scenario B: Forms
   * If the result item is not a link, we assume that we are in the context
   * of a form and set a hidden input value that can be used
   * to send along with a form submission.
   *
   * * @param selected The active result item
   */
  commit(selected: Element) {
    this.resetResults();
    if (selected instanceof HTMLAnchorElement) {
      return;
    }

    const { listboxOptionValue, listboxOptionLabel } = (selected as any)
      .dataset;

    this.selectedValue = listboxOptionValue;
    this.selectedOption = selected;
    this.$refs.searchInput.value = listboxOptionLabel;
    this.$refs.searchInput.focus();

    this.$dispatch("auto-complete:change", {
      value: listboxOptionValue,
      label: listboxOptionLabel,
      selected: selected,
      inputName: this.config.autoCompleteInputName,
    });
  },

  async fetchResults(query: string) {
    try {
      this.$dispatch("auto-complete:loadstart", {
        inputName: this.config.autoCompleteInputName,
      });
      this.results.loading = true;
      const queryParamObj = {};
      queryParamObj[this.config.autoCompleteQueryParam] = query;
      const response = await get(this.config.autoCompleteUrl, {
        query: queryParamObj,
      });

      if (response.ok) {
        this.replaceResults(await response.html);
        this.$dispatch("auto-complete:load", {
          inputName: this.config.autoCompleteInputName,
        });
        this.$dispatch("auto-complete:loadend", {
          inputName: this.config.autoCompleteInputName,
        });
      } else {
        const responseError =
          (response && response.statusText) || response.status;
        throw Error(responseError);
      }
    } catch (error) {
      this.$dispatch("auto-complete:error", {
        message: error.message,
        inputName: this.config.autoCompleteInputName,
      });
      this.$dispatch("auto-complete:loadend", {
        inputName: this.config.autoCompleteInputName,
      });
    } finally {
      this.results.loading = false;
    }
  },

  /**
   * Renders the list of search results in the form of a menu component returned
   * from the server. If a menu is already present, it is replaced. The first
   * search result menu item is highlighted as active by default.
   *
   * @param html - The HTML containing the menu of search results.
   */
  replaceResults(html: string) {
    this.results.content = html;

    this.$nextTick(() => {
      this.results.visible = true;
      this.selectSibling(true);
    });
  },

  resetResults() {
    this.results.visible = false;
  },

  get options(): Element[] {
    return Array.from(
      this.$refs.resultsContainer.querySelectorAll(optionSelector)
    );
  },
});
